import {
  AbstractControl,
  AsyncValidatorFn,
  FormArray,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { catchError, delay, from, map, of, switchMap } from 'rxjs';

export const errorObjectBuilder = (message: string) => ({
  message,
});

/**
 * @return {ValidatorFn}
 */
export function urlValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const urlPattern =
      /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,})(:\d{2,5})?(\/\S*)*$/i;
    const errorObject = errorObjectBuilder('invalid-url');
    const givenUrl: string = control.value;
    if (givenUrl === '' || !givenUrl) {
      return null;
    } else if (urlPattern.test(givenUrl)) {
      return null;
    } else {
      return errorObject;
    }
  };
}

/**
 * Validator for point rescue codes
 * @param {Function} asyncFunction
 * @param {string} errorMessage translatable error message
 * @return {AsyncValidatorFn}
 */
export function asyncValidator(
  asyncFunction: (arg0: any) => Promise<boolean>,
  errorMessage: string
): AsyncValidatorFn {
  return (control: AbstractControl) =>
    of(control.value).pipe(
      delay(500),
      switchMap((value) =>
        from(asyncFunction(value)).pipe(
          map((ok) => (ok ? null : errorObjectBuilder(errorMessage))),
          catchError((err) => of(errorObjectBuilder(err)))
        )
      )
    );
}

/**
 * Searches a form array for its own value, erroring if a value is already present
 * @param {FormArray} formArray
 * @param {Function} getValue
 * @return {ValidatorFn}
 */
export function uniqueControlFormArrayValidator(
  formArray: FormArray,
  getValue?: (arg0: AbstractControl) => any
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    getValue = getValue || ((innerControl) => innerControl.value);
    let ok = true;
    let occurrences = 0;

    for (const c of formArray.controls) {
      if (getValue(c) === control.value) {
        if (++occurrences === 2) {
          ok = false;
          break;
        }
      }
    }
    return ok ? null : errorObjectBuilder('field-not-unique');
  };
}

/**
 * Makes sure some control is not larger than biggerControl
 * @param {AbstractControl} biggerControl
 * @param {string} errorMessage
 * @return {ValidatorFn}
 */
export function lessThanOrEqualToValidator(
  biggerControl: AbstractControl,
  errorMessage: string = 'field-not-smaller'
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control?.value == null || biggerControl?.value == null) {
      return null;
    }
    const ok = control.value <= biggerControl.value;
    return ok ? null : errorObjectBuilder(errorMessage);
  };
}

/**
 * Rejects date values with year before 1900, supporting both native Date and Moment.js objects
 * (MomentDateAdapter stores Moment objects in FormControls, which use .year() not .getFullYear())
 * @return {ValidatorFn}
 */
export function validDateValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null) return null;
    const year: number =
      typeof (value as any).year === 'function'
        ? (value as any).year()
        : (value as Date).getFullYear();
    if (year < 1900) {
      return errorObjectBuilder('invalid-date');
    }
    return null;
  };
}

export const passwordMinLength = 8;

/**
 * Decides if a field contains a valid password
 * @return {ValidatorFn}
 */
export function passwordValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const password: string = control.value;
    const minLengthOk = password?.length >= passwordMinLength;
    const errors: {
      'invalid-password-too-short'?: string;
      'invalid-password-no-numbers'?: string;
      'invalid-password-no-special-chars'?: string;
      'invalid-password-no-uppercase'?: string;
      'invalid-password-no-lowercase'?: string;
    } = {};
    if (!minLengthOk) {
      errors['invalid-password-too-short'] = 'invalid-password-too-short';
    }
    const hasNumbers = Boolean(password?.match(/\d/));
    if (!hasNumbers) {
      errors['invalid-password-no-numbers'] = 'invalid-password-no-numbers';
    }
    const hasSpecialChars = Boolean(
      password?.match(/[\^$*.[ \]{}()?"!@#%&/\\,><':;|_~`=+-]/)
    );
    if (!hasSpecialChars) {
      errors['invalid-password-no-special-chars'] =
        'invalid-password-no-special-chars';
    }
    const hasUpperCase = Boolean(password?.match(/[A-Z]/));
    if (!hasUpperCase) {
      errors['invalid-password-no-uppercase'] = 'invalid-password-no-uppercase';
    }
    const hasLowerCase = Boolean(password?.match(/[a-z]/));
    if (!hasLowerCase) {
      errors['invalid-password-no-lowercase'] = 'invalid-password-no-lowercase';
    }
    if (
      errors['invalid-password-too-short'] ||
      errors['invalid-password-no-numbers'] ||
      errors['invalid-password-no-special-chars'] ||
      errors['invalid-password-no-uppercase'] ||
      errors['invalid-password-no-lowercase']
    ) {
      return errors;
    }
    return null;
  };
}

/**
 * Decides if a field has some exact value
 * @param {any} original value that must be exactly equal to validate
 * @return {ValidatorFn}
 */
export function exactValidator(original: any): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return control.value === original
      ? null
      : errorObjectBuilder('invalid-fields-do-not-match');
  };
}

/**
 * Invalidates field if its value is already present in @list
 * @param {T[]} list
 * @param {function(el: T): string} getter
 * @return {ValidatorFn}
 */
export function notInValidator<T>(
  list?: T[],
  getter?: (el: T) => string
): ValidatorFn {
  getter = getter || ((el) => el as unknown as string);
  return (control: AbstractControl): ValidationErrors | null => {
    if (!list) {
      return null;
    }
    const valueExists = list.find(
      (el: T) => getter!(el).trim() === control.value.trim()
    );
    return valueExists
      ? errorObjectBuilder('inserted-value-already-present')
      : null;
  };
}

/**
 * Check if serial is valid through some rules
 * @return {ValidatorFn}
 */
export function serialValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const serial = control.value as string;
    if (serial.length > 11) {
      return null;
    }
    const valid = /^[a-zA-Z0-9]{5}[XYZxyz0-9]{1}\d{4}[a-zA-Z0-9]?$/.test(
      serial
    );
    return valid ? null : errorObjectBuilder('invalid-serial-error-message');
  };
}

/**
 * Decides if a field contains no special chars
 * @return {ValidatorFn}
 */
export function specialCharValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const input: string = control.value;
    const error = ['invalid-special-char-error-message'];
    const hasSpecialChars = Boolean(input?.match(/['"<>+=@`{}&\\/]/));
    if (hasSpecialChars) {
      return error;
    }
    return null;
  };
}

/**
 * Decides if a field contains no HTML chars
 * @return {ValidatorFn}
 */
export function htmlCharValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const input: string = control.value;
    const error = { 'invalid-html-char-error-message': true };
    const hasSpecialChars = Boolean(input?.match(/['"<>+=@`{}&\\/]/));
    if (hasSpecialChars) {
      return error;
    }
    return null;
  };
}

/**
 * Checks email pattern
 * @return {ValidatorFn}
 */
export function emailPattern(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const input: string = control.value;
    const error = ['invalid-email'];
    if (!input) return null;
    const regExp = new RegExp(
      /[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]/.source +
        /(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@/.source +
        /(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?/
          .source
    );
    const isValidEmail = Boolean(input?.match(regExp));

    return isValidEmail ? null : error;
  };
}

/**
 * Custom validator to check if the input is not just blank spaces and meets the minimum length
 * requirement when the spaces are trimmed.
 * @param {number} minLength - The minimum length required for the trimmed input.
 * @return {{minimumTrimmedLength: boolean} | null} A validator function.
 */
export function minimumTrimmedLength(minLength: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value?.trim().replace(/\s+/g, ' ');
    if (!value || value.length < minLength) {
      return { minimumTrimmedLength: { requiredLength: minLength } };
    }
    return null;
  };
}

/**
 * Validator to ensure not all countries are selected across all regions.
 * If all available countries are selected, the form will be marked as invalid.
 *
 * @param {any} regionList - The full list of available regions and their countries.
 * @return {{allCountriesSelected: boolean} | null} A validator function.
 */
export function allCountriesSelectedValidator(
  regionList: { countries: { id: number }[] }[]
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const selectedRegions = control.value || [];
    const error = ['invalid-exceptions-selection'];
    const selectedCountryIds = selectedRegions.flatMap((r: any) =>
      r.countries.map((c: any) => c.id)
    );
    const allCountryIds = regionList.flatMap((region) =>
      region.countries.map((c) => c.id)
    );
    const allSelected = selectedCountryIds.length === allCountryIds.length;
    if (allSelected) {
      return error;
    }
    return null;
  };
}

/**
 * Validator to ensure at least min VIP checkboxes are selected, otherwise marks form as invalid.
 *
 * @param {number} min - The minimum number of checkboxes that must be selected.
 * @return {ValidatorFn} A validator function.
 */
export function vipCheckboxesValidator(min: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (Array.isArray(value) && value.length >= min) {
      return null;
    }
    return {
      minArrayLength: {
        requiredLength: min,
        actualLength: value?.length || 0,
        message: 'At least one VIP Level must be selected.',
      },
    };
  };
}

/**
 * Validator to ensure at least one header is present in either 'headers' or 'oldHeaders' fields.
 *
 * @param {AbstractControl} group - The form group containing 'headers' and 'oldHeaders'.
 * @return {ValidationErrors | null} Validation error if neither field has headers, otherwise null.
 */
export function atLeastOneHeaderValidator(
  group: AbstractControl
): ValidationErrors | null {
  const headers = group.get('headers')?.value as string[] | null;
  const oldHeaders = group.get('oldHeaders')?.value as string[] | null;

  const hasHeaders = Array.isArray(headers) && headers.length > 0;
  const hasOldHeaders = Array.isArray(oldHeaders) && oldHeaders.length > 0;

  return hasHeaders || hasOldHeaders
    ? null
    : { atLeastOneHeaderRequired: true };
}
